// BUILD-042 — one fetch of the Equipment & Property schedule per employee view, shared by the Overview tile and the detail
// list so their counts and rows can never disagree. Read-only: the schedule is derived on the server from generated
// equipment checkouts and their document status.
import { useCallback, useEffect, useState } from 'react';
import { getHrEquipmentPropertyForEmployee, getMyHrEquipmentProperty, type HrPropertySchedule } from '../../../api/client';
import { classifyLoadError } from './loadState';

export type PropertyLoad =
  | { status: 'loading' }
  // Oct 24: `kind` (admin mode only) says WHY it failed: 'denied' = explicit 403, 'targetUnavailable' = the intentionally non-enumerating 404, 'unavailable' = the request failed.
  | { status: 'error'; message: string; kind?: 'denied' | 'targetUnavailable' | 'unavailable' }
  | { status: 'ok'; schedule: HrPropertySchedule };

/** mode 'self' = the signed-in employee's own schedule; 'admin' = HR viewing `employeeId` (server enforces document-management authority). */
export function usePropertySchedule(mode: 'self' | 'admin', employeeId: string | null | undefined): { load: PropertyLoad; reload: () => void } {
  const [load, setLoad] = useState<PropertyLoad>({ status: 'loading' });
  const [tick, setTick] = useState(0);
  const reload = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (mode === 'admin' && !employeeId) return;
    let cancelled = false;
    const request = mode === 'self' ? getMyHrEquipmentProperty() : getHrEquipmentPropertyForEmployee(employeeId as string);
    request
      .then((schedule) => { if (!cancelled) setLoad({ status: 'ok', schedule }); })
      .catch((e: any) => {
        if (cancelled) return;
        const kind = mode === 'admin' ? classifyLoadError(e, { targeted: true }).kind : 'unavailable';
        setLoad({ status: 'error', message: e?.message || 'Could not load equipment and property.', kind: kind === 'denied' || kind === 'targetUnavailable' ? kind : 'unavailable' });
      });
    return () => { cancelled = true; };
  }, [mode, employeeId, tick]);

  return { load, reload };
}
